import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Check, Plus, X } from "lucide-react";
import {
  catalogLabelFor,
  type CatalogKind,
  type CatalogOption,
  type ProductTypeBehavior,
} from "@nh/shared";
import { Button, Input, Select, SelectField, type SelectOption } from "@/shared/ui/core";
import { isApiError } from "@/shared/api/client";
import { useCatalogOptions, useCreateCatalogOption } from "./catalog.queries";

export interface CatalogSelectFieldProps {
  /** Ruta del campo en el formulario: `"category"`, `"type"`. */
  name: string;
  /** Qué lista se está eligiendo. */
  kind: CatalogKind;
  label: string;
  hint?: string;
  /** El hueco de "todavía no elegí". */
  placeholder?: string;
  required?: boolean;
  /** Lo que se lee en el botón de añadir: "Añadir categoría". */
  addLabel: string;
  /**
   * Ofrece además una DESCRIPCIÓN al dar de alta una opción.
   *
   * La piden los costos adicionales —"Envío express" y qué significa—
   * y no la piden las categorías. Es un prop y no un `kind === …` para
   * que quien añada la sexta lista decida si su opción se explica sin
   * tener que tocar este archivo.
   */
  describable?: boolean;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * El selector de un CATÁLOGO DINÁMICO, con "añadir" sin salir del paso.
 *
 * ── Qué arregla ────────────────────────────────────────────────────
 * Antes, la categoría era un campo de texto libre con sugerencias, y las
 * sugerencias salían de los productos que YA existían. Con la base vacía
 * no había ninguna y no había forma de crear la primera; con la base
 * llena, "Detox" y "detox" entraban como dos.
 *
 * ── Por qué es UN componente y no dos pantallas ─────────────────────
 * La alternativa era mandar a quien administra a una pantalla de
 * categorías y traerlo de vuelta. En un formulario de diez pasos eso
 * significa abandonar lo escrito a mitad de camino: react-hook-form
 * conserva los valores entre PASOS, no entre pantallas. Añadir aquí
 * mismo es la diferencia entre crear una categoría y perder un producto.
 *
 * ── Y por qué es GENÉRICO ───────────────────────────────────────────
 * La categoría y el tipo de ítem eran el mismo control, y las dos listas
 * que faltaban —BENEFICIOS y COSTOS ADICIONALES— también lo son. Las
 * cuatro usan este archivo: cuatro copias con el `kind` cambiado es
 * exactamente lo que este repositorio evita, y el día que el alta
 * necesite un aviso o una confirmación tres se quedarían sin él.
 *
 * Lo único que cambia entre listas son dos preguntas del alta, y las dos
 * son props: un tipo de ítem declara su COMPORTAMIENTO (`kind ===
 * "product_type"`) y un costo admite una DESCRIPCIÓN (`describable`).
 *
 * ── Y dentro de una lista que crece ─────────────────────────────────
 * Los beneficios y los costos son VARIOS por producto, no uno. No hacía
 * falta un selector múltiple nuevo: cada fila de un `RepeatableList` es
 * uno de estos, con su `name` propio (`benefits.0.value`). Así se
 * conserva algo que un multiselector no da — el ORDEN, que es
 * información: el primer beneficio se lee siempre y el sexto casi nunca.
 * ═══════════════════════════════════════════════════════════════════
 */
export function CatalogSelectField({
  name,
  kind,
  label,
  hint,
  placeholder,
  required,
  addLabel,
  describable = false,
}: CatalogSelectFieldProps) {
  const { t, i18n } = useTranslation();
  const { setValue } = useFormContext();
  const current = useWatch({ name }) as string | undefined;

  const options = useCatalogOptions({ kind });
  const create = useCreateCatalogOption();

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [behavior, setBehavior] = useState<ProductTypeBehavior>("product");
  /**
   * La opción recién creada, esperando a poder elegirse.
   *
   * ── Por qué no se elige y ya ───────────────────────────────────────
   * Un `<select>` no puede tomar un valor cuya `<option>` todavía no
   * existe en el documento: la asignación no hace nada y el campo se
   * queda en blanco. Y cuando el alta responde, la lista de opciones
   * sigue siendo la de antes — llega en el render SIGUIENTE.
   *
   * Sin esta espera, crear una categoría la dejaba creada y sin elegir:
   * el formulario decía tener valor y la pantalla enseñaba el hueco
   * vacío. Nada fallaba, que es lo que lo hacía difícil de ver.
   *
   * Aquí se apunta cuál hay que elegir y el efecto de abajo la elige en
   * cuanto aparece entre las opciones.
   */
  const [pendingValue, setPendingValue] = useState<string | null>(null);

  const locale = i18n.language.startsWith("en") ? "en" : "es";

  /**
   * Lo que se ofrece: las ACTIVAS, más la que este producto ya tiene
   * puesta aunque esté archivada.
   *
   * La segunda mitad es la que evita el fallo silencioso: una categoría
   * retirada sigue siendo válida en las fichas que la usan, y si el
   * desplegable no la incluyera, abrir uno de esos productos lo
   * enseñaría "sin categoría" — y guardarlo se la quitaría de verdad,
   * sin que nadie hubiera pedido nada.
   */
  const selectOptions = useMemo<SelectOption[]>(() => {
    const items = options.data?.items ?? [];
    const visibles = items.filter(
      (item: CatalogOption) =>
        /*
         * Por `kind` TAMBIÉN aquí, aunque la petición ya lo filtre.
         *
         * No es paranoia: la caché de react-query se comparte por clave,
         * y basta con que otro sitio pida la lista entera para que esta
         * respuesta traiga las dos. Sin este filtro, el desplegable de
         * categorías ofrecería "Paquete" y "Promoción" — que se guardaría
         * sin error y clasificaría el producto en una categoría que no
         * existe.
         */
        item.kind === kind && (item.status === "active" || item.value === current),
    );
    return visibles.map((item: CatalogOption) => ({
      value: item.value,
      label:
        item.status === "archived"
          ? t("{{name}} (retirada)", { name: catalogLabelFor(item.label, locale) })
          : catalogLabelFor(item.label, locale),
    }));
  }, [options.data, current, kind, locale, t]);

  useEffect(() => {
    if (!pendingValue) return;
    if (!selectOptions.some((option) => option.value === pendingValue)) return;
    /*
     * `shouldDirty` para que cuente como un cambio de verdad: sin él,
     * "Guardar cambios" podría creer que no hay nada que guardar.
     */
    setValue(name, pendingValue, { shouldDirty: true, shouldValidate: true });
    setPendingValue(null);
  }, [pendingValue, selectOptions, name, setValue]);

  function cancel(): void {
    setAdding(false);
    setDraft("");
    setDraftDescription("");
    setAddError(null);
  }

  async function confirm(): Promise<void> {
    const nombre = draft.trim();
    if (nombre.length < 2) {
      setAddError(t("Escribe un nombre con al menos dos letras."));
      return;
    }

    try {
      const option = await create.mutateAsync({
        kind,
        label: { es: nombre },
        sortOrder: 0,
        ...(kind === "product_type" ? { behavior } : {}),
        ...(describable && draftDescription.trim()
          ? { description: { es: draftDescription.trim() } }
          : {}),
      });
      /*
       * Se ELIGE sola la recién creada. Crearla y dejar el desplegable
       * como estaba obliga a buscarla en una lista de cuarenta justo
       * después de haberla escrito. La elección la hace el efecto de
       * arriba, cuando la opción ya existe en el documento.
       */
      setPendingValue(option.value);
      cancel();
    } catch (error) {
      /*
       * El 409 del servidor —"esa categoría ya existe"— se pinta AQUÍ, en
       * el renglón donde se escribió, y no en el cartel de arriba del
       * formulario: el mensaje trae el nombre de la que ya existe y sirve
       * de nada a diez pasos de distancia.
       */
      setAddError(
        isApiError(error) ? error.message : t("No se pudo crear. Vuelve a intentarlo."),
      );
    }
  }

  /**
   * Enter confirma; Escape cancela.
   *
   * `stopPropagation` no es un adorno: el formulario del producto captura
   * Enter para AVANZAR DE PASO. Sin esto, escribir "Detox" y pulsar Enter
   * saltaría al paso 3 con la categoría a medio crear.
   */
  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      void confirm();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      cancel();
    }
  }

  return (
    <div className="grid gap-1.5">
      {/*
        El botón de añadir va EN LA FILA DE LA ETIQUETA, no en un renglón
        debajo del desplegable.
        
        Medido: cada botón bajo su campo costaba 36 px, y con dos
        catálogos en el mismo paso son 72 px — que es justo lo que
        separaba al paso 2 de caber en una vista de 1440×900. Además se
        lee mejor: "Categoría · + Añadir" dice que la lista se puede
        ampliar antes de abrirla, en vez de después de mirarla entera.
      */}
      <SelectField
        name={name}
        label={label}
        labelAction={
          adding ? undefined : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex items-center gap-1 text-[11px] font-medium text-ds-violet-pop hover:underline"
            >
              <Plus aria-hidden="true" className="h-3 w-3" />
              {addLabel}
            </button>
          )
        }
        hint={hint}
        required={required}
        options={selectOptions}
        placeholder={placeholder}
        disabled={options.isPending || undefined}
      />

      {adding && (
        <div className="grid gap-1.5 rounded-xl border border-ds-border bg-ds-cream-2/40 p-2.5">
          <Input
            autoFocus
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setAddError(null);
            }}
            onKeyDown={handleKeyDown}
            label={addLabel}
            placeholder={t("Detox")}
            error={addError ?? undefined}
            disabled={create.isPending}
          />

          {/*
            La descripción de la opción, cuando la lista la usa. Va en el
            CATÁLOGO y no en el producto porque explica lo mismo en las
            cuarenta fichas que la ofrezcan: escribirla por ficha sería
            copiar el mismo párrafo cuarenta veces y corregir la errata
            cuarenta veces.

            Solo en español al crear: es el idioma obligatorio en todo el
            catálogo, y la traducción se añade después desde la pantalla
            de opciones sin bloquear el alta.
          */}
          {describable && (
            <Input
              value={draftDescription}
              onChange={(event) => setDraftDescription(event.target.value)}
              onKeyDown={handleKeyDown}
              label={t("Descripción (opcional)")}
              placeholder={t("Entrega en 24 h con guía rastreable")}
              disabled={create.isPending}
            />
          )}

          {/*
            Un tipo de ítem NUEVO tiene que decir qué hace: de eso depende
            qué campos pide el resto del paso. El servidor lo exige
            también —ver `createOption`— pero preguntarlo aquí evita el
            viaje de ida y vuelta para descubrirlo.
          */}
          {kind === "product_type" && (
            <Select
              label={t("¿Qué campos pide?")}
              value={behavior}
              onChange={(event) => setBehavior(event.target.value as ProductTypeBehavior)}
              options={[
                { value: "product", label: t("Los de un producto: existencias") },
                { value: "package", label: t("Los de un paquete: qué lo compone") },
                { value: "promotion", label: t("Los de una promoción: descuento y fechas") },
              ]}
              disabled={create.isPending}
            />
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              leftIcon={<Check />}
              loading={create.isPending}
              onClick={() => void confirm()}
            >
              {t("Guardar")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              leftIcon={<X />}
              onClick={cancel}
            >
              {t("Cancelar")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
